import { cn } from "@/lib/utils";

export type AvatarProps = {
  src?: string | null;
  alt?: string;
  initials?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  variant?: "circle" | "rounded";
  bordered?: boolean;
  status?: "online" | "offline" | "away" | "busy";
  className?: string;
};

const sizeClasses = {
  xs: "w-6 h-6 text-[10px] font-medium",
  sm: "w-8 h-8 text-xs font-medium",
  md: "w-10 h-10 text-sm font-semibold",
  lg: "w-12 h-12 text-base font-semibold",
  xl: "w-16 h-16 text-xl font-bold",
};

const variantClasses = {
  circle: "rounded-full",
  rounded: "rounded-lg",
};

const indicatorSizes = {
  xs: "w-2 h-2",
  sm: "w-2.5 h-2.5",
  md: "w-3 h-3",
  lg: "w-3.5 h-3.5",
  xl: "w-4 h-4",
};

const statusColors = {
  online: "bg-green-500",
  offline: "bg-surface-500",
  away: "bg-yellow-500",
  busy: "bg-red-500",
};

export function Avatar({
  src,
  alt = "",
  initials,
  size = "md",
  variant = "circle",
  bordered = false,
  status,
  className = "",
}: AvatarProps) {
  const baseClasses = cn(
    "inline-flex items-center justify-center overflow-hidden shrink-0",
    sizeClasses[size],
    variantClasses[variant],
  );

  return (
    <div className="relative inline-block">
      {src ? (
        <img
          src={src}
          alt={alt}
          className={cn(
            baseClasses,
            "object-cover",
            bordered && "border-2 border-brand-800",
            className,
          )}
        />
      ) : (
        <div
          className={cn(
            baseClasses,
            "bg-surface-700 text-content-secondary",
            bordered && "border-2 border-brand-800",
            className,
          )}
        >
          {initials?.slice(0, 3).toUpperCase()}
        </div>
      )}
      {status && (
        <span
          className={cn(
            "absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-surface-900",
            indicatorSizes[size],
            statusColors[status],
          )}
        />
      )}
    </div>
  );
}
